import { Button, Col, DatePicker, notification, Row, Table, Tag, Tooltip } from "antd";
import { FilePdfOutlined, SendOutlined } from '@ant-design/icons';
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import { assignedDietRangeApi } from "../../../../../../../config/Api";
import { apiErrorMessage } from "../../../../../../../services/ApiError";
import { DIET_STATUS_EVENT, openDietPdf, sendDiet } from "./dietStatus";
import { UnsentDietBanner } from "./UnsentDietBanner";

const STATUS = {
    sent: { color: 'green', label: 'Sent' },
    not_sent: { color: 'orange', label: 'Not sent' },
    changed: { color: 'orange', label: 'Changed since sent' },
    no_diet: { color: 'default', label: 'No diet' },
}

// the patient's assigned diets for a week (or any range): what each meal is, and whether it was sent
export const AssignedDiets = ({ onChange }) => {
    const { id } = useParams()
    const [range, setRange] = useState([moment(), moment().add(6, 'days')])
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [sending, setSending] = useState(false)

    const load = () => {
        if (!range?.[0] || !range?.[1]) return
        setLoading(true)
        assignedDietRangeApi(id, range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD'))
            .then((res) => setData(res.data.data))
            .catch((err) => notification.error({ message: apiErrorMessage(err) }))
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        load()
        window.addEventListener(DIET_STATUS_EVENT, load)
        return () => window.removeEventListener(DIET_STATUS_EVENT, load)
    }, [range, id])

    const days = data?.days || []
    const toSend = days.filter((day) => day.status === 'not_sent' || day.status === 'changed').map((day) => day.date)
    // days the patient already got, which can be sent again
    const sentDays = days.filter((day) => day.status === 'sent').map((day) => day.date)
    const withDiet = days.filter((day) => day.has_diet)

    // sending: which button is busy ('new', 'resend' or a date)
    const send = (dates, key) => {
        setSending(key)
        sendDiet(id, dates).finally(() => setSending(false))
    }

    const columns = [
        {
            title: 'Day',
            key: 'day',
            fixed: 'left',
            render: (_, day) => (
                <>
                    <b>{day.label}</b>
                    <div><Tag color={STATUS[day.status]?.color} className="mt-1">{STATUS[day.status]?.label}</Tag></div>
                    {day.sent_at && day.status !== 'not_sent' && (
                        <div className="text-muted" style={{ fontSize: 12 }}>Sent {moment(day.sent_at).format('DD MMM, hh:mm A')}</div>
                    )}
                    {day.status === 'sent' && (
                        <Button
                            type="link"
                            size="small"
                            className="p-0"
                            loading={sending === day.date}
                            disabled={!data?.can_send || Boolean(sending)}
                            onClick={() => send([day.date], day.date)}
                        >
                            {sending === day.date ? 'Sending…' : 'Resend'}
                        </Button>
                    )}
                </>
            ),
        },
        ...(data?.meal_times || []).map((mealTime, index) => ({
            title: mealTime,
            key: mealTime,
            render: (_, day) => {
                const meal = day.meals[index]
                if (!day.has_diet) return index === 0 ? <span className="text-muted">No diet assigned</span> : null
                if (meal.state === 'not_assigned') return <span className="text-muted">Not assigned</span>
                if (meal.state === 'none') return <span className="text-muted">-</span>
                return meal.recipes.map((name) => <div key={name}>{name}</div>)
            },
        })),
    ]

    return (
        <div className="AssignedDiets">
            <UnsentDietBanner patientId={id} />
            <Row gutter={[12, 12]} className="mb-3" align="middle">
                <Col>
                    <DatePicker.RangePicker
                        value={range}
                        onChange={setRange}
                        format="DD-MM-YYYY"
                        allowClear={false}
                        disabledDate={(current) => range?.[0] && current && Math.abs(current.diff(range[0], 'days')) > 30}
                    />
                </Col>
                <Col flex="auto" className="text-end">
                    <Tooltip title={!data?.can_send ? "The patient hasn't verified their email yet." : !toSend.length ? 'Everything in these days is already sent.' : ''}>
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            className="me-2"
                            loading={sending === 'new'}
                            disabled={!data?.can_send || !toSend.length || Boolean(sending)}
                            onClick={() => send(toSend, 'new')}
                        >
                            {sending === 'new' ? 'Sending…' : `Send to patient${toSend.length ? ` (${toSend.length} day${toSend.length === 1 ? '' : 's'})` : ''}`}
                        </Button>
                    </Tooltip>
                    <Tooltip title={!data?.can_send ? "The patient hasn't verified their email yet." : !sentDays.length ? 'Nothing has been sent in these days yet.' : 'Email these days to the patient again.'}>
                        <Button
                            icon={<SendOutlined />}
                            className="me-2"
                            loading={sending === 'resend'}
                            disabled={!data?.can_send || !sentDays.length || Boolean(sending)}
                            onClick={() => send(sentDays, 'resend')}
                        >
                            {sending === 'resend' ? 'Sending…' : `Resend sent days${sentDays.length ? ` (${sentDays.length})` : ''}`}
                        </Button>
                    </Tooltip>
                    <Tooltip title={withDiet.length ? '' : 'No diet in these days.'}>
                        <Button
                            icon={<FilePdfOutlined />}
                            disabled={!withDiet.length}
                            onClick={() => openDietPdf(id, range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD'))}
                        >
                            View PDF
                        </Button>
                    </Tooltip>
                </Col>
            </Row>
            <Table
                rowKey="date"
                loading={loading}
                columns={columns}
                dataSource={days}
                pagination={false}
                bordered
                scroll={{ x: 'max-content' }}
            />
            {days.length > 0 && !withDiet.length && (
                <p className="text-muted mt-3">
                    No diet is assigned in these days. <Button type="link" className="p-0" onClick={() => onChange && onChange('6')}>Assign a diet</Button>
                </p>
            )}
        </div>
    )
}
